import React from 'react';
import { Link } from "react-router-dom";
import routes from 'src/routes';
import url from 'src/util/url';
import Page from 'src/components/Page';
import ExpandDoc from 'src/components/ExpandDoc';
import CodeSamples from 'src/components/CodeSamples';
import SpecLink from 'src/components/SpecLink';

function TokenAdvancedJavaScriptPage() {
    return <Page header="JavaScript - Advanced">
        <p>
            The JavaScript Advanced integration method allows you to integrate with greater control of the form and user interaction.
            You handle the creation of the form and submission of card data.
        </p>

        <p>
            Creating a token using the JavaScript Advanced approach requires that you:
            <ol>
                <li><SpecLink uriFragment="#operation/TokenAuthkey_CreateAuthkey">Create authkey</SpecLink></li>
                <li><SpecLink uriFragment="#operation/TokenAuthkey_AttachTokenDetails">Attach token details</SpecLink></li>
                <li>Using <b>JavaScript Advanced</b>, attach payment details.</li>
                <li><SpecLink uriFragment="#operation/TokenAuthkey_Process">Process token</SpecLink></li>
            </ol> 
        </p>

        <h2>Attaching payment details using JavaScript Advanced</h2>

        <h3>Html page</h3>
        <h4>Add JavaScript</h4>
        <p>To enable JavaScript integration, you must include the following script tag in your page.</p>
        <CodeSamples
            samples={[
                {
                    language: 'html',
                    content: `<script src="${url.api("clientscripts/api.js")}" type="text/javascript"></script>`
                }]} copyButton />

        <h4>Add payment control</h4>
        <p>Unlike <Link to={routes.api.token.authkey.javascriptSimple}>JavaScript Simple</Link>, you will need to create your own html payment control.  A payment control can obtain payment details for either credit card, bank account or both.</p>
        <p>
            Credit card must have the following fields:
            <ul>
                <li>Card number</li>
                <li>Expiry month</li>
                <li>Expiry year</li>
                <li>Card holder name</li>
            </ul>
        </p>
        <p>
            Bank account must have the following fields:
            <ul>
                <li>BSB</li>
                <li>Account number</li>
                <li>Account name</li>
            </ul>
        </p>

        <h4>Add styling</h4>
        <p>Unlike <Link to={routes.api.token.authkey.javascriptSimple}>JavaScript Simple</Link>, you will need to create your own CSS style for your html payment control.</p>

        <h3>JavaScript</h3>
        <p>The next step is to pass those payment details to BPOINT via a call to <code>BPOINT.token.authkey.attachPaymentMethod</code>.</p>

        <p>Once payment details have been attached, you may process the token after the <code>success</code> status on the <code>callback</code> function is returned.</p>
        <p>Here is an example for attaching Credit Card details:</p>
        <CodeSamples
            samples={[
                {
                    language: 'javascript',
                    content: `
BPOINT.token.authkey.attachPaymentMethod(authkey,
    {
        card: {
            number: document.querySelector("#cardNumberField").value,
            expiry: {
                month: document.querySelector("#expiryMonthField").value,
                year: document.querySelector("#expiryYearField").value
            },
            name: document.querySelector("#nameOnCardField").value

        },
        bank: undefined
    },
    function (code, data) {            
        if (code === "success") {
            // Add your code here to process your token.  You may choose to process your token later on but you must receive a success status on the callback first, otherwise Process Token will fail.
        } else {
            // handle any errors!
        }
    }
); 
                    `
                }]} />
        
        <h2 id="Specification">Specification</h2>
        <ExpandDoc
            method="BPOINT.token.authkey.attachPaymentMethod"
            doc={{
                authkey: {
                    type: 'string',
                    description: "Transaction authkey to attach payment method to"
                },
                paymentMethod: {
                    type: 'object',
                    description: <>This request is similiar to <Link to={routes.api.spec + "#operation/TokenAuthkeyClient_AttachPaymentMethod"}>TokenAuthkeyClient - AttachPaymentMethod</Link></>,
                    children: {
                        card: {
                            type: "object",
                            description: "Card details",
                            children: {
                                number: {
                                    type: "string",
                                    description: "Card number",        
                                },
                                expiry: {
                                    type: "string",
                                    description: "Card expiry",        
                                    children: {
                                        month: {
                                            type: "string",
                                            description: "expiry month",        
                                        },                                        
                                        year: {
                                            type: "string",
                                            description: "expiry year",
                                        }
                                    }
                                },
                                name: {
                                    type: "string",
                                    description: "Card holder name",        
                                }

                            }
                        },
                        bank: {
                            type: "object",
                            description: "Bank account details",
                            children: {
                                bsb: {
                                    type: "string",
                                    description: "BSB",        
                                },
                                account: {
                                    type: "string",
                                    description: "Account number",        
                                },
                                name: {
                                    type: "string",
                                    description: "Account name",        
                                },
                            }
                        }
                    }
                },
                callback: {
                    type: "function",
                    description: "A callback function that is executed when the submission result is received",
                    children: {
                        code: {
                            type: "string",
                            description: <>One of <code>success</code>, <code>error</code>, or <code>timeout</code></>
                        },
                        data: {
                            type: "object",
                            description: <>This response is similiar to <Link to={routes.api.spec + "#operation/TokenAuthkeyClient_AttachPaymentMethod"}>TokenAuthkeyClient - AttachPaymentMethod</Link></>,
                            children: {
                                card: {
                                    type: "object",
                                    description: "Card details",
                                    children: {
                                        number: {
                                            type: "string",
                                            description: "Masked card number",        
                                        },
                                        expiry: {
                                            type: "string",
                                            description: "Card expiry",        
                                            children: {
                                                month: {
                                                    type: "string",
                                                    description: "expiry month",        
                                                },                                        
                                                year: {
                                                    type: "string",
                                                    description: "expiry year",
                                                }
                                            }
                                        },
                                        name: {
                                            type: "string",
                                            description: "Card holder name",
                                        },
                                        scheme: {
                                            type: "string",
                                            description: "Card scheme, for example Mastercard, Visa, Amex etc.",
                                        },
                                        localisation: {
                                            type: "string",
                                            description: "Local or International card.",
                                        },
                                        type: {
                                            type: "string",
                                            description: "Credit or Debit card type.",
                                        }        
                                    }
                                },
                                bank: {
                                    type: "object",
                                    description: "Bank account details",
                                    children: {
                                        bsb: {
                                            type: "string",
                                            description: "BSB",        
                                        },
                                        account: {
                                            type: "string",
                                            description: "Masked account number",
                                        },
                                        name: {
                                            type: "string",
                                            description: "Account name",        
                                        },
                                    }        
                                }
                            }        
                        }
                    }
                },
                settings: {
                    type: "object",                    
                    description: "Allow Submit button to be disabled once payment details are accepted.",
                    children: {
                        submitButton: {
                            type: "object",
                            description: "Details of the Submit button and its behaviour",
                            children: {
                                selector: {
                                    type: "string",
                                    description: <>Html Id of the Submit button to be disabled.  For example, if your button is called <code>btnSubmit</code>, the value should be <code>#btnSubmit</code></>,
                                },
                                disableOnClick: {
                                    type: "boolean",
                                    description: 'If set to true, the Submit button will be disabled once payment details are accepted'
                                }
                            }
                        }
                    }
                }
            }}
        />

        <p><code>BPOINT.token.authkey.getPaymentMethod</code> allows you to obtain information about a customer's payment details.  Credit card and bank account numbers are masked (for example: 512345...346).</p>
        <ExpandDoc
            method="BPOINT.token.authkey.getPaymentMethod"
            doc={{
                authkey: {
                    type: 'string',
                    description: "Transaction authkey to attach payment method to"
                },
                callback: {
                    type: "function",
                    description: "A callback function that is executed when the submission result is received",
                    children: {
                        code: {
                            type: "string",
                            description: <>One of <code>success</code>, <code>error</code>, or <code>timeout</code></>
                        },
                        data: {
                            type: "object",
                            description: <>See response of <Link to={routes.api.spec + "#operation/TokenAuthkeyClient_GetPaymentMethod"}>TokenAuthkeyClient - GetPaymentMethod</Link></>
                        }
                    }
                }
            }}
        />

        <h2>Updating payment details for a token</h2>
        <p>
            To update the payment details for a token, you will need the pass the Token number into <SpecLink uriFragment="#operation/TokenAuthkey_CreateAuthkey">Create a token authkey</SpecLink>.
        </p>
    </Page>
}

export default TokenAdvancedJavaScriptPage;